import { useState, useCallback } from 'react'
import { post } from '../api/client'
import { useMode } from '../context/ModeContext'
import { useBusinessContext } from '../context/BusinessContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePlaidLink } from 'react-plaid-link'
import {
  getSettings,
  addCategory, renameCategory, deleteCategory,
  addSubCategory, renameSubCategory, deleteSubCategory,
  addTag, renameTag, deleteTag,
  getBusinesses, createBusiness, updateBusiness,
  getClients, createClient, updateClient, deleteClient,
  getBizServices, createBizService, updateBizService, deleteBizService,
  getFiscalYearSettings, updateFiscalYearSettings,
  getPlaidItems, unlinkPlaidAccount, createLinkToken, exchangePlaidToken, updateAccountSettings,
  reorderPlaidItems, reorderPlaidAccounts, updateAccountNickname,
} from '../api'
import type { Category, Tag, Business, Client, BizService, FiscalYearSettings, PlaidItem, PlaidAccountInfo } from '../types'

// ── Shared style constants ────────────────────────────────────────────────────
const S = {
  iconBtn: (hover: boolean, variant: 'edit' | 'delete' | 'none'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 8, border: 'none',
    background: hover
      ? variant === 'edit'   ? 'rgba(99,102,241,0.15)'
      : variant === 'delete' ? 'rgba(239,68,68,0.15)'
      : 'transparent'
      : 'transparent',
    color: hover
      ? variant === 'edit'   ? 'var(--pb-primary)'
      : variant === 'delete' ? '#ef4444'
      : 'var(--pb-text)'
      : 'var(--pb-muted)',
    cursor: 'pointer',
    transition: 'background 0.12s ease, color 0.12s ease',
    fontSize: '0.82rem',
  }),
  saveBtn: (hover: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
    background: hover ? 'rgba(34,197,94,0.28)' : 'rgba(34,197,94,0.15)',
    color: '#22c55e',
    transition: 'background 0.12s ease',
    fontSize: '0.82rem',
  }),
  cancelBtn: (hover: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
    background: hover ? 'rgba(148,163,184,0.18)' : 'transparent',
    color: 'var(--pb-muted)',
    transition: 'background 0.12s ease',
    fontSize: '0.82rem',
  }),
  inlineInput: {
    flex: 1, padding: '0.3rem 0.6rem', borderRadius: 8,
    border: '1.5px solid #818cf8',
    background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)',
    fontSize: '0.92rem', fontWeight: 500,
    boxShadow: '0 0 0 3px rgba(99,102,241,0.12)',
    outline: 'none',
    minWidth: 0,
  } as React.CSSProperties,
  addInput: {
    flex: 1, padding: '0.35rem 0.7rem', borderRadius: 8,
    border: '1.5px solid var(--pb-border)',
    background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)',
    fontSize: '0.88rem', outline: 'none',
  } as React.CSSProperties,
  addBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.35rem 0.8rem', borderRadius: 8,
    fontSize: '0.82rem', fontWeight: 600,
    background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)', border: 'none', cursor: 'pointer',
  } as React.CSSProperties,
}

// ── Toast ─────────────────────────────────────────────────────────────────────
interface ToastMsg { id: number; message: string; isError: boolean }

function useToast() {
  const [toasts, setToasts] = useState<ToastMsg[]>([])
  const show = useCallback((message: string, isError = false) => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, isError }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000)
  }, [])
  return { toasts, show }
}

function ToastContainer({ toasts }: { toasts: ToastMsg[] }) {
  if (!toasts.length) return null
  return (
    <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '0.5rem', pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '0.65rem 1.1rem', borderRadius: 12,
          fontSize: '0.88rem', fontWeight: 600,
          boxShadow: '0 8px 24px -8px rgba(0,0,0,0.3)',
          background: t.isError ? '#fef2f2' : '#f0fdf4',
          color: t.isError ? '#dc2626' : '#16a34a',
          display: 'flex', alignItems: 'center', gap: 7,
        }}>
          <i className={`bi ${t.isError ? 'bi-exclamation-circle' : 'bi-check-circle'}`} />
          {t.message}
        </div>
      ))}
    </div>
  )
}

// ── Reusable hoverable buttons ────────────────────────────────────────────────
function IBtn({ variant, title, onClick }: { variant: 'edit' | 'delete'; title: string; onClick: () => void }) {
  const [h, setH] = useState(false)
  return (
    <button title={title} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={S.iconBtn(h, variant)}>
      <i className={variant === 'edit' ? 'bi bi-pencil' : 'bi bi-trash'} />
    </button>
  )
}
function SaveBtn({ onClick, loading }: { onClick: () => void; loading?: boolean }) {
  const [h, setH] = useState(false)
  return (
    <button title="Save" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={S.saveBtn(h)}>
      {loading ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <i className="bi bi-check2" />}
    </button>
  )
}
function CancelBtn({ onClick }: { onClick: () => void }) {
  const [h, setH] = useState(false)
  return (
    <button title="Cancel" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={S.cancelBtn(h)}>
      <i className="bi bi-x-lg" style={{ fontSize: '0.72rem' }} />
    </button>
  )
}

// ── Add row (dashed separator, input revealed on button click) ────────────────
function AddRow({ placeholder, icon, onAdd, indent }: {
  placeholder: string; icon: string; onAdd: (name: string) => Promise<void>; indent?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [v, setV] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    if (!v.trim()) return
    setLoading(true)
    try { await onAdd(v.trim()); setV(''); setOpen(false) }
    finally { setLoading(false) }
  }

  const ROW_STYLE: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.65rem',
    padding: indent ? '0.6rem 1.4rem 0.6rem 3.2rem' : '0.6rem 1.4rem',
    borderTop: '1px dashed var(--pb-border)',
  }

  if (!open) return (
    <div style={ROW_STYLE}>
      <button style={S.addBtn} onClick={() => setOpen(true)}>
        <i className="bi bi-plus-lg" style={{ fontSize: '0.78rem' }} />
        {placeholder}
      </button>
    </div>
  )

  return (
    <div style={ROW_STYLE}>
      <i className={`bi ${icon}`} style={{ color: 'var(--pb-muted)', fontSize: '0.9rem', flexShrink: 0 }} />
      <input
        autoFocus
        value={v}
        onChange={e => setV(e.target.value)}
        placeholder={placeholder.replace('Add ', 'New ') + '…'}
        style={S.addInput}
        onKeyDown={async e => {
          if (e.key === 'Enter') await submit()
          if (e.key === 'Escape') { setV(''); setOpen(false) }
        }}
      />
      <SaveBtn onClick={submit} loading={loading} />
      <CancelBtn onClick={() => { setV(''); setOpen(false) }} />
    </div>
  )
}

// ── Categories section ────────────────────────────────────────────────────────
function CategoriesSection({ categories, onToast, businessId }: {
  categories: Category[]
  onToast: (msg: string, isError?: boolean) => void
  businessId?: number | null
}) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [editingCat, setEditingCat] = useState<number | null>(null)
  const [editCatVal, setEditCatVal] = useState('')
  const [editCatLoading, setEditCatLoading] = useState(false)
  const [editingSub, setEditingSub] = useState<number | null>(null)
  const [editSubVal, setEditSubVal] = useState('')
  const [editSubLoading, setEditSubLoading] = useState(false)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['settings', businessId ?? null] })

  const toggle = (id: number) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const startEditCat = (id: number, name: string) => { setEditingCat(id); setEditCatVal(name); setEditingSub(null) }
  const cancelEditCat = () => setEditingCat(null)
  const saveCat = async () => {
    if (!editCatVal.trim() || editingCat === null) return
    setEditCatLoading(true)
    try { await renameCategory(editingCat, editCatVal.trim()); invalidate(); onToast(`Renamed to "${editCatVal.trim()}"`); setEditingCat(null) }
    catch { onToast('Failed to rename category', true) }
    finally { setEditCatLoading(false) }
  }

  const startEditSub = (id: number, name: string) => { setEditingSub(id); setEditSubVal(name); setEditingCat(null) }
  const cancelEditSub = () => setEditingSub(null)
  const saveSub = async () => {
    if (!editSubVal.trim() || editingSub === null) return
    setEditSubLoading(true)
    try { await renameSubCategory(editingSub, editSubVal.trim()); invalidate(); onToast(`Renamed to "${editSubVal.trim()}"`); setEditingSub(null) }
    catch { onToast('Failed to rename subcategory', true) }
    finally { setEditSubLoading(false) }
  }

  const CAT_ROW: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.8rem 1.4rem', borderBottom: '1px solid var(--pb-border)',
  }
  const SUB_ROW: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.65rem',
    padding: '0.55rem 1.4rem 0.55rem 3.2rem',
    borderBottom: '1px solid var(--pb-border)',
    background: 'var(--pb-surface, #fafbff)',
  }

  return (
    <div style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20, overflow: 'hidden' }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--pb-border)' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
            <i className="bi bi-tags me-2" style={{ color: 'var(--pb-muted)' }} />Categories
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: '0.15rem' }}>
            {categories.length} {categories.length === 1 ? 'category' : 'categories'} · {categories.reduce((n, c) => n + c.subCategories.length, 0)} subcategories
          </div>
        </div>
        <AddRow placeholder="Add Category" icon="bi-tags" onAdd={async name => { await addCategory(name, businessId); invalidate(); onToast(`"${name}" added`) }} />
      </div>

      {/* Category rows */}
      {categories.length === 0 && (
        <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--pb-muted)' }}>
          <i className="bi bi-tags" style={{ fontSize: '2rem', opacity: 0.35 }} />
          <div className="mt-2 fw-semibold">No categories yet</div>
          <div style={{ fontSize: '0.85rem' }}>Add your first category to get started.</div>
        </div>
      )}

      {categories.map(cat => {
        const dotColor = cat.color ?? '#6366f1'
        const isOpen = expanded.has(cat.id)
        const isEditingThis = editingCat === cat.id
        return (
          <div key={cat.id}>
            <div style={CAT_ROW}>
              {/* Chevron */}
              <button
                onClick={() => toggle(cat.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.85rem', color: isOpen ? '#6366f1' : 'var(--pb-muted)', transition: 'transform 0.2s ease, color 0.15s ease', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', lineHeight: 1, flexShrink: 0 }}
              >
                <i className="bi bi-chevron-right" />
              </button>

              {/* Color dot */}
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />

              {/* Name or inline input */}
              {isEditingThis ? (
                <input
                  autoFocus
                  value={editCatVal}
                  onChange={e => setEditCatVal(e.target.value)}
                  style={S.inlineInput}
                  onKeyDown={async e => { if (e.key === 'Enter') await saveCat(); if (e.key === 'Escape') cancelEditCat() }}
                />
              ) : (
                <span style={{ flex: 1, fontWeight: 600, fontSize: '0.95rem', color: 'var(--pb-text)' }}>{cat.name}</span>
              )}

              {/* Actions slot */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                {isEditingThis ? (
                  <>
                    <SaveBtn onClick={saveCat} loading={editCatLoading} />
                    <CancelBtn onClick={cancelEditCat} />
                  </>
                ) : cat.isSystem ? (
                  <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: 'var(--pb-border)', color: 'var(--pb-muted)', border: '1px solid var(--pb-border)' }}>
                    System
                  </span>
                ) : (
                  <>
                    <IBtn variant="edit" title="Rename" onClick={() => startEditCat(cat.id, cat.name)} />
                    <IBtn variant="delete" title="Delete" onClick={() => {
                      if (window.confirm(`Delete category "${cat.name}"?\n\nExisting transactions will keep their text label.`)) {
                        deleteCategory(cat.id).then(invalidate).then(() => onToast(`"${cat.name}" deleted`)).catch(() => onToast('Failed to delete', true))
                      }
                    }} />
                  </>
                )}
              </div>
            </div>

            {/* Subcategory list */}
            {isOpen && (
              <div>
                {cat.subCategories.map(sub => {
                  const isEditingSub = editingSub === sub.id
                  return (
                    <div key={sub.id} style={SUB_ROW}>
                      <i className="bi bi-arrow-return-right" style={{ color: 'var(--pb-muted)', fontSize: '0.8rem', flexShrink: 0 }} />
                      {isEditingSub ? (
                        <input
                          autoFocus
                          value={editSubVal}
                          onChange={e => setEditSubVal(e.target.value)}
                          style={S.inlineInput}
                          onKeyDown={async e => { if (e.key === 'Enter') await saveSub(); if (e.key === 'Escape') cancelEditSub() }}
                        />
                      ) : (
                        <span style={{ flex: 1, fontSize: '0.88rem', color: 'var(--pb-text)' }}>{sub.name}</span>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                        {isEditingSub ? (
                          <>
                            <SaveBtn onClick={saveSub} loading={editSubLoading} />
                            <CancelBtn onClick={cancelEditSub} />
                          </>
                        ) : (
                          <>
                            <IBtn variant="edit" title="Rename" onClick={() => startEditSub(sub.id, sub.name)} />
                            <IBtn variant="delete" title="Delete" onClick={() => {
                              if (window.confirm(`Delete subcategory "${sub.name}"?`)) {
                                deleteSubCategory(sub.id).then(invalidate).then(() => onToast(`"${sub.name}" deleted`)).catch(() => onToast('Failed to delete', true))
                              }
                            }} />
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
                <AddRow
                  placeholder="Add Subcategory"
                  icon="bi-diagram-3"
                  indent
                  onAdd={async name => { await addSubCategory(cat.id, name); invalidate(); onToast(`"${name}" added`) }}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Tags section ──────────────────────────────────────────────────────────────
function TagsSection({ tags, onToast, businessId }: {
  tags: Tag[]
  onToast: (msg: string, isError?: boolean) => void
  businessId?: number | null
}) {
  const qc = useQueryClient()
  const [editingTag, setEditingTag] = useState<number | null>(null)
  const [editTagVal, setEditTagVal] = useState('')
  const [editTagLoading, setEditTagLoading] = useState(false)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['settings', businessId ?? null] })

  const startEdit = (id: number, name: string) => { setEditingTag(id); setEditTagVal(name) }
  const cancelEdit = () => setEditingTag(null)
  const saveTag = async () => {
    if (!editTagVal.trim() || editingTag === null) return
    setEditTagLoading(true)
    try { await renameTag(editingTag, editTagVal.trim()); invalidate(); onToast(`Renamed to "${editTagVal.trim()}"`); setEditingTag(null) }
    catch { onToast('Failed to rename tag', true) }
    finally { setEditTagLoading(false) }
  }

  const ROW: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.8rem 1.4rem', borderBottom: '1px solid var(--pb-border)',
  }

  return (
    <div style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20, overflow: 'hidden' }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--pb-border)' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
            <i className="bi bi-bookmark me-2" style={{ color: 'var(--pb-muted)' }} />Tags
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: '0.15rem' }}>
            {tags.length} {tags.length === 1 ? 'tag' : 'tags'}
          </div>
        </div>
        <AddRow placeholder="Add Tag" icon="bi-bookmark" onAdd={async name => { await addTag(name, businessId); invalidate(); onToast(`"${name}" added`) }} />
      </div>

      {/* Tag rows */}
      {tags.length === 0 && (
        <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--pb-muted)' }}>
          <i className="bi bi-bookmarks" style={{ fontSize: '2rem', opacity: 0.35 }} />
          <div className="mt-2 fw-semibold">No tags yet</div>
          <div style={{ fontSize: '0.85rem' }}>Add your first tag to get started.</div>
        </div>
      )}

      {tags.map(tag => {
        const isEditing = editingTag === tag.id
        return (
          <div key={tag.id} style={ROW}>
            {isEditing ? (
              <input
                autoFocus
                value={editTagVal}
                onChange={e => setEditTagVal(e.target.value)}
                style={S.inlineInput}
                onKeyDown={async e => { if (e.key === 'Enter') await saveTag(); if (e.key === 'Escape') cancelEdit() }}
              />
            ) : (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.22rem 0.6rem', borderRadius: 999,
                fontSize: '0.8rem', fontWeight: 600,
                background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)', border: '1px solid rgba(99,102,241,0.35)',
              }}>
                <i className="bi bi-bookmark-fill" style={{ fontSize: '0.7rem' }} />
                {tag.name}
              </span>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
              {isEditing ? (
                <>
                  <SaveBtn onClick={saveTag} loading={editTagLoading} />
                  <CancelBtn onClick={cancelEdit} />
                </>
              ) : (
                <>
                  <IBtn variant="edit" title="Rename" onClick={() => startEdit(tag.id, tag.name)} />
                  <IBtn variant="delete" title="Delete" onClick={() => {
                    if (window.confirm(`Delete tag "${tag.name}"?\n\nExisting transactions will lose this tag.`)) {
                      deleteTag(tag.id).then(invalidate).then(() => onToast(`"${tag.name}" deleted`)).catch(() => onToast('Failed to delete', true))
                    }
                  }} />
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}


// ── Business section ──────────────────────────────────────────────────────────

const EMPTY_BIZ: Partial<Business> = {
  businessName: '', contactName: '', country: 'Canada',
  taxRate: 0.13, currency: 'CAD', invoicePrefix: 'INV',
}

// ── GeneralSection ────────────────────────────────────────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December']

function GeneralSection({ onToast }: { onToast: (msg: string, isError?: boolean) => void }) {
  const qc = useQueryClient()
  const { data: fy, isLoading } = useQuery({
    queryKey: ['fiscal-year-settings'],
    queryFn: getFiscalYearSettings,
    staleTime: 60_000,
  })

  const [form, setForm] = useState<FiscalYearSettings | null>(null)
  const [saving, setSaving] = useState(false)

  // Sync form from fetched data once
  const loaded = !!fy && !form
  if (loaded) setForm({ ...fy })

  const set = <K extends keyof FiscalYearSettings>(k: K, v: FiscalYearSettings[K]) =>
    setForm((f: FiscalYearSettings | null) => f ? { ...f, [k]: v } : f)

  const save = async () => {
    if (!form) return
    setSaving(true)
    try {
      await updateFiscalYearSettings(form)
      qc.invalidateQueries({ queryKey: ['fiscal-year-settings'] })
      onToast('Fiscal year settings saved.')
    } catch { onToast('Failed to save fiscal year settings.', true) }
    finally { setSaving(false) }
  }

  const F: React.CSSProperties = {
    padding: '0.38rem 0.65rem', borderRadius: 8,
    border: '1.5px solid var(--pb-border)',
    background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)',
    fontSize: '0.88rem', outline: 'none',
  }

  const current = form ?? fy

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.25rem' }}>General Settings</div>
      <p style={{ color: 'var(--pb-muted)', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
        App-wide settings that apply across all businesses.
      </p>

      {isLoading && <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>}

      {current && (
        <div className="pb-card" style={{ padding: '1.4rem', maxWidth: 540 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem' }}>
            <i className="bi bi-calendar3 me-2" style={{ color: '#6366f1' }} />Fiscal Year
          </div>

          {/* Calendar year toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <input
              type="checkbox"
              id="fy-calendar"
              checked={current.followsCalendar}
              onChange={e => set('followsCalendar', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <label htmlFor="fy-calendar" style={{ fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', margin: 0 }}>
              Follows calendar year (Jan 1 – Dec 31)
            </label>
          </div>

          {/* Custom month + day picker */}
          {!current.followsCalendar && (
            <div style={{ marginLeft: '1.75rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--pb-muted)', marginBottom: '0.4rem' }}>
                Fiscal Year Starts On
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select style={{ ...F, width: 160 }} value={current.startMonth} onChange={e => set('startMonth', parseInt(e.target.value))}>
                  {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <input type="number" min={1} max={31} style={{ ...F, width: 72 }}
                  value={current.startDay}
                  onChange={e => set('startDay', Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))} />
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--pb-muted)' }}>
                FY is named after the year it <em>ends</em> in.{' '}
                Example: {MONTH_NAMES[(current.startMonth ?? 4) - 1]} {current.startDay},{' '}
                2026 → <strong>FY 2027</strong>
              </div>
            </div>
          )}

          <button
            onClick={save}
            disabled={saving || !form}
            style={{ marginTop: '0.5rem', padding: '0.45rem 1.1rem', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg, var(--pb-primary) 0%, var(--pb-primary-2,#7c3aed) 100%)', color: '#fff', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> : 'Save'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── SecuritySection ───────────────────────────────────────────────────────────
function SecuritySection({ onToast }: { onToast: (msg: string, isError?: boolean) => void }) {
  const [form,    setForm]    = useState({ current: '', next: '', confirm: '' })
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const valid =
    form.current.length > 0 &&
    form.next.length >= 8 &&
    form.next === form.confirm

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      await post('/auth/change-password', {
        currentPassword: form.current,
        newPassword:     form.next,
      })
      setForm({ current: '', next: '', confirm: '' })
      onToast('Password changed.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change password.'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const F: React.CSSProperties = {
    padding: '0.38rem 0.65rem', borderRadius: 8,
    border: '1.5px solid var(--pb-border)',
    background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)',
    fontSize: '0.88rem', outline: 'none', width: '100%',
  }

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.25rem' }}>Security</div>
      <p style={{ color: 'var(--pb-muted)', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
        Change your login password.
      </p>

      <div className="pb-card" style={{ padding: '1.4rem', maxWidth: 400 }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem' }}>
          <i className="bi bi-shield-lock-fill me-2" style={{ color: '#6366f1' }} />Change Password
        </div>

        {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          <div>
            <label className="form-label small fw-medium mb-1" htmlFor="cp-current">Current password</label>
            <input id="cp-current" type="password" style={F} value={form.current}
              autoComplete="current-password"
              onChange={e => setForm(f => ({ ...f, current: e.target.value }))} />
          </div>
          <div>
            <label className="form-label small fw-medium mb-1" htmlFor="cp-new">New password</label>
            <input id="cp-new" type="password" style={F} value={form.next}
              autoComplete="new-password"
              onChange={e => setForm(f => ({ ...f, next: e.target.value }))} />
            <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', marginTop: 4 }}>
              Minimum 8 characters
            </div>
          </div>
          <div>
            <label className="form-label small fw-medium mb-1" htmlFor="cp-confirm">Confirm new password</label>
            <input id="cp-confirm" type="password" style={F} value={form.confirm}
              autoComplete="new-password"
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
            {form.confirm.length > 0 && form.next !== form.confirm && (
              <div style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4 }}>Passwords do not match</div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '1.25rem' }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={save}
            disabled={!valid || saving}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
              : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  )
}

function BusinessSection({ onToast }: { onToast: (msg: string, isError?: boolean) => void }) {
  const qc = useQueryClient()
  const [adding, setAdding]   = useState(false)
  const [newForm, setNewForm] = useState<Partial<Business>>({ ...EMPTY_BIZ })
  const [creating, setCreating] = useState(false)
  const { selectedBusiness } = useBusinessContext()
  const biz = selectedBusiness

  const handleCreate = async () => {
    if (!newForm.businessName?.trim() || !newForm.contactName?.trim()) { onToast('Business name and contact name are required', true); return }
    setCreating(true)
    try { await createBusiness(newForm); await qc.invalidateQueries({ queryKey: ['businesses'] }); setAdding(false); setNewForm({ ...EMPTY_BIZ }); onToast('Business created') }
    catch { onToast('Failed to create business', true) }
    finally { setCreating(false) }
  }

  const F: React.CSSProperties = { width: '100%', padding: '0.38rem 0.65rem', borderRadius: 8, border: '1.5px solid var(--pb-border)', background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }
  const lbl = (t: string) => <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--pb-muted)', marginBottom: 3 }}>{t}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => { setAdding(a => !a); if (!adding) setNewForm({ ...EMPTY_BIZ }) }} style={{ padding: '0.35rem 0.85rem', borderRadius: 8, border: '1.5px dashed var(--pb-border)', background: 'transparent', color: 'var(--pb-muted)', fontSize: '0.84rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <i className="bi bi-plus-lg" style={{ fontSize: '0.75rem' }} />Add Business
        </button>
      </div>
      {adding && (
        <div style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 14, padding: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.75rem' }}>New Business</div>
          <div className="row g-2">
            <div className="col-sm-6">{lbl('Business Name *')}<input style={F} value={newForm.businessName ?? ''} onChange={e => setNewForm(f => ({ ...f, businessName: e.target.value }))} /></div>
            <div className="col-sm-6">{lbl('Contact Name *')}<input style={F} value={newForm.contactName ?? ''} onChange={e => setNewForm(f => ({ ...f, contactName: e.target.value }))} /></div>
            <div className="col-sm-6">{lbl('Email')}<input style={F} value={newForm.email ?? ''} onChange={e => setNewForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div className="col-sm-3">{lbl('Currency')}<input style={F} placeholder="CAD" value={newForm.currency ?? ''} onChange={e => setNewForm(f => ({ ...f, currency: e.target.value }))} /></div>
            <div className="col-sm-3">{lbl('Invoice Prefix')}<input style={F} placeholder="INV" value={newForm.invoicePrefix ?? ''} onChange={e => setNewForm(f => ({ ...f, invoicePrefix: e.target.value }))} /></div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button onClick={() => setAdding(false)} style={{ padding: '0.35rem 0.8rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--pb-muted)', fontSize: '0.84rem' }}>Cancel</button>
            <button onClick={handleCreate} disabled={creating} style={{ padding: '0.35rem 0.9rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)', fontWeight: 600, fontSize: '0.84rem' }}>
              {creating ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> : 'Create Business'}
            </button>
          </div>
        </div>
      )}
      {biz
        ? <BusinessProfile key={biz.businessID} biz={biz} onToast={onToast} onSaved={() => qc.invalidateQueries({ queryKey: ['businesses'] })} />
        : !adding && <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--pb-muted)', background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20 }}><i className="bi bi-building" style={{ fontSize: '2rem', opacity: 0.35 }} /><div className="mt-2 fw-semibold">No business selected</div><div style={{ fontSize: '0.85rem' }}>Select a business from the navigation bar.</div></div>
      }
    </div>
  )
}


function BusinessProfile({ biz, onToast, onSaved }: { biz: Business; onToast: (m: string, e?: boolean) => void; onSaved: () => void }) {
  const [form, setForm] = useState({ ...biz })
  const [saving, setSaving] = useState(false)
  const set = (k: keyof Business, v: string | number) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    try { await updateBusiness(biz.businessID, form); onSaved(); onToast('Business profile saved') }
    catch { onToast('Failed to save', true) }
    finally { setSaving(false) }
  }

  const F: React.CSSProperties = {
    width: '100%', padding: '0.42rem 0.7rem', borderRadius: 8,
    border: '1.5px solid var(--pb-border)',
    background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)',
    fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
  }
  const lbl = (text: string) => <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--pb-muted)', marginBottom: '0.25rem' }}>{text}</div>

  return (
    <div style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20, overflow: 'hidden' }}>
      <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--pb-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}><i className="bi bi-building me-2" style={{ color: 'var(--pb-muted)' }} />Business Profile</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: '0.15rem' }}>Your consulting entity details used on invoices.</div>
        </div>
        <button onClick={save} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 1rem', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)' }}>
          {saving ? <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> : <i className="bi bi-check2" />}
          Save
        </button>
      </div>
      <div style={{ padding: '1.4rem', display: 'grid', gap: '1rem' }}>
        <div className="row g-3">
          <div className="col-sm-6">{lbl('Business Name')}<input style={F} value={form.businessName ?? ''} onChange={e => set('businessName', e.target.value)} /></div>
          <div className="col-sm-6">{lbl('Contact Name')}<input style={F} value={form.contactName ?? ''} onChange={e => set('contactName', e.target.value)} /></div>
          <div className="col-sm-6">{lbl('Title')}<input style={F} value={form.title ?? ''} onChange={e => set('title', e.target.value)} placeholder="e.g. Enterprise Solutions Architect" /></div>
          <div className="col-sm-6">{lbl('Email')}<input style={F} value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></div>
          <div className="col-sm-6">{lbl('Phone')}<input style={F} value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} placeholder="e.g. +1 (416) 555-0100" /></div>
        </div>
        <div style={{ borderTop: '1px solid var(--pb-border)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--pb-muted)', marginBottom: '0.75rem' }}>Address</div>
          <div className="row g-3">
            <div className="col-sm-8">{lbl('Street Address')}<input style={F} value={form.address1 ?? ''} onChange={e => set('address1', e.target.value)} placeholder="123 Main Street" /></div>
            <div className="col-sm-4">{lbl('Suite / Unit')}<input style={F} value={form.address2 ?? ''} onChange={e => set('address2', e.target.value)} placeholder="Suite 200" /></div>
            <div className="col-sm-4">{lbl('City')}<input style={F} value={form.city ?? ''} onChange={e => set('city', e.target.value)} /></div>
            <div className="col-sm-3">{lbl('Province / State')}<input style={F} value={form.province ?? ''} onChange={e => set('province', e.target.value)} placeholder="ON" /></div>
            <div className="col-sm-2">{lbl('Postal Code')}<input style={F} value={form.postalCode ?? ''} onChange={e => set('postalCode', e.target.value)} placeholder="A1A 1A1" /></div>
            <div className="col-sm-3">{lbl('Country')}<input style={F} value={form.country ?? ''} onChange={e => set('country', e.target.value)} placeholder="Canada" /></div>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-sm-6">{lbl('HST / GST Number')}<input style={F} value={form.hstNumber ?? ''} onChange={e => set('hstNumber', e.target.value)} placeholder="e.g. 123456789 RT0001" /></div>
          <div className="col-sm-3">{lbl('Tax Rate')}<input style={F} type="number" step="0.0001" value={form.taxRate} onChange={e => set('taxRate', parseFloat(e.target.value) || 0)} /></div>
          <div className="col-sm-3">{lbl('Currency')}<input style={F} value={form.currency ?? ''} onChange={e => set('currency', e.target.value)} placeholder="CAD" /></div>
          <div className="col-sm-6">{lbl('Invoice Prefix')}<input style={F} value={form.invoicePrefix ?? ''} onChange={e => set('invoicePrefix', e.target.value)} placeholder="e.g. EC" /></div>
          <div className="col-sm-6">{lbl('Default Payment Method')}<input style={F} value={form.defaultPaymentMethod ?? ''} onChange={e => set('defaultPaymentMethod', e.target.value)} placeholder="e.g. Bank transfer / EFT" /></div>
          <div className="col-sm-6">{lbl('Default Payment Terms')}<input style={F} value={form.defaultPaymentTerms ?? ''} onChange={e => set('defaultPaymentTerms', e.target.value)} placeholder="e.g. Net 30" /></div>
          <div className="col-12">{lbl('Tagline')}<input style={F} value={form.tagLine ?? ''} onChange={e => set('tagLine', e.target.value)} placeholder="e.g. Enterprise Solutions Architecture" /></div>
          <div className="col-12">{lbl('Footer Text')}<input style={F} value={form.footerText ?? ''} onChange={e => set('footerText', e.target.value)} /></div>
        </div>
      </div>
    </div>
  )
}

// ── Clients manager ───────────────────────────────────────────────────────────
// ── Client full form (shared for add and edit) ────────────────────────────────
function ClientForm({ value, onChange, F }: {
  value: Partial<Client>
  onChange: (f: Partial<Client>) => void
  F: React.CSSProperties
}) {
  const lbl = (t: string) => <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--pb-muted)', marginBottom: 3 }}>{t}</div>
  const s = (k: keyof Client) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value })
  return (
    <div className="row g-2">
      <div className="col-sm-2">{lbl('Client # *')}<input style={F} placeholder="ACME" value={value.clientNumber ?? ''} onChange={s('clientNumber')} /></div>
      <div className="col-sm-10">{lbl('Company Name *')}<input style={F} placeholder="Acme Corp" value={value.clientName ?? ''} onChange={s('clientName')} /></div>
      <div className="col-sm-4">{lbl('Contact Name')}<input style={F} placeholder="Jane Smith" value={value.contactName ?? ''} onChange={s('contactName')} /></div>
      <div className="col-sm-4">{lbl('Email')}<input style={F} placeholder="jane@acme.com" value={value.email ?? ''} onChange={s('email')} /></div>
      <div className="col-sm-4">{lbl('Phone')}<input style={F} placeholder="+1 (555) 000-0000" value={value.phone ?? ''} onChange={s('phone')} /></div>
      <div className="col-sm-8">{lbl('Address Line 1')}<input style={F} placeholder="123 Main St" value={value.address1 ?? ''} onChange={s('address1')} /></div>
      <div className="col-sm-4">{lbl('Address Line 2')}<input style={F} placeholder="Suite 400" value={value.address2 ?? ''} onChange={s('address2')} /></div>
      <div className="col-sm-4">{lbl('City')}<input style={F} placeholder="Toronto" value={value.city ?? ''} onChange={s('city')} /></div>
      <div className="col-sm-3">{lbl('Province / State')}<input style={F} placeholder="ON" value={value.province ?? ''} onChange={s('province')} /></div>
      <div className="col-sm-2">{lbl('Postal Code')}<input style={F} placeholder="M5V 2T6" value={value.postalCode ?? ''} onChange={s('postalCode')} /></div>
      <div className="col-sm-3">{lbl('Country')}<input style={F} placeholder="Canada" value={value.country ?? 'Canada'} onChange={s('country')} /></div>
      <div className="col-sm-4">{lbl('Payment Terms')}<input style={F} placeholder="Net 30" value={value.defaultPaymentTerms ?? ''} onChange={s('defaultPaymentTerms')} /></div>
      <div className="col-sm-2">{lbl('Currency')}<input style={F} placeholder="CAD" value={value.currency ?? ''} onChange={s('currency')} /></div>
    </div>
  )
}

function ClientsManager({ businessId, onToast }: { businessId: number; onToast: (m: string, e?: boolean) => void }) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['biz-clients', businessId] })

  const { data: clients = [] } = useQuery({
    queryKey: ['biz-clients', businessId],
    queryFn: () => getClients(businessId),
    staleTime: 30_000,
  })

  const [editing, setEditing] = useState<number | null>(null)
  const [form, setForm] = useState<Partial<Client>>({})
  const [adding, setAdding] = useState(false)
  const [addForm, setAddForm] = useState<Partial<Client>>({ country: 'Canada', isActive: true })
  const [saving, setSaving] = useState(false)

  const startEdit = (c: Client) => { setEditing(c.clientID); setForm({ ...c }) }
  const cancelEdit = () => setEditing(null)

  const saveEdit = async () => {
    if (!editing) return
    setSaving(true)
    try { await updateClient(editing, form as Client); invalidate(); onToast('Client updated'); setEditing(null) }
    catch { onToast('Failed to update client', true) }
    finally { setSaving(false) }
  }

  const saveAdd = async () => {
    if (!addForm.clientNumber?.trim() || !addForm.clientName?.trim()) { onToast('Client number and name are required', true); return }
    setSaving(true)
    try { await createClient(businessId, addForm); invalidate(); onToast('Client added'); setAdding(false); setAddForm({ country: 'Canada', isActive: true }) }
    catch { onToast('Failed to add client', true) }
    finally { setSaving(false) }
  }

  const F: React.CSSProperties = { padding: '0.3rem 0.55rem', borderRadius: 7, border: '1.5px solid var(--pb-border)', background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)', fontSize: '0.85rem', outline: 'none', width: '100%' }

  return (
    <div style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--pb-border)' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}><i className="bi bi-people me-2" style={{ color: 'var(--pb-muted)' }} />Clients</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: '0.15rem' }}>{clients.length} client{clients.length !== 1 ? 's' : ''}</div>
        </div>
        {!adding && !editing && <button onClick={() => setAdding(true)} style={S.addBtn}><i className="bi bi-plus-lg" style={{ fontSize: '0.78rem' }} />Add Client</button>}
      </div>

      {/* Add form */}
      {adding && (
        <div style={{ padding: '1.2rem 1.4rem', borderBottom: '1px solid var(--pb-border)', background: 'var(--pb-surface, #fafbff)' }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.85rem', color: 'var(--pb-text)' }}>New Client</div>
          <ClientForm value={addForm} onChange={setAddForm} F={F} />
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.85rem' }}>
            <button onClick={() => { setAdding(false); setAddForm({ country: 'Canada', isActive: true }) }} style={{ padding: '0.35rem 0.8rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--pb-muted)', fontSize: '0.84rem' }}>Cancel</button>
            <button onClick={saveAdd} disabled={saving} style={{ padding: '0.35rem 0.9rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)', fontWeight: 600, fontSize: '0.84rem' }}>
              {saving ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : 'Save Client'}
            </button>
          </div>
        </div>
      )}

      {clients.length === 0 && !adding && (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--pb-muted)' }}>
          <i className="bi bi-people" style={{ fontSize: '2rem', opacity: 0.35 }} />
          <div className="mt-2 fw-semibold">No clients yet</div>
          <div style={{ fontSize: '0.85rem' }}>Add your first client to start creating invoices.</div>
        </div>
      )}

      {clients.map(cl => (
        <div key={cl.clientID} style={{ borderBottom: '1px solid var(--pb-border)' }}>
          {editing === cl.clientID ? (
            <div style={{ padding: '1.2rem 1.4rem', background: 'var(--pb-surface, #fafbff)' }}>
              <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.85rem' }}>Edit — {cl.clientName}</div>
              <ClientForm value={form} onChange={setForm} F={F} />
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.85rem' }}>
                <button onClick={cancelEdit} style={{ padding: '0.3rem 0.7rem', borderRadius: 7, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--pb-muted)', fontSize: '0.84rem' }}>Cancel</button>
                <SaveBtn onClick={saveEdit} loading={saving} />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1.4rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 6, background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)', letterSpacing: '0.04em', flexShrink: 0 }}>{cl.clientNumber}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{cl.clientName}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--pb-muted)', marginTop: 1 }}>
                  {[cl.contactName, cl.email, [cl.city, cl.province].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                </div>
              </div>
              <IBtn variant="edit" title="Edit" onClick={() => startEdit(cl)} />
              <IBtn variant="delete" title="Delete" onClick={() => {
                if (window.confirm(`Delete client "${cl.clientName}"?`)) {
                  deleteClient(cl.clientID).then(invalidate).then(() => onToast(`"${cl.clientName}" deleted`)).catch(() => onToast('Failed to delete', true))
                }
              }} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Services manager ──────────────────────────────────────────────────────────
function ServicesManager({ businessId, onToast }: { businessId: number; onToast: (m: string, e?: boolean) => void }) {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['biz-services', businessId] })

  const { data: services = [] } = useQuery({
    queryKey: ['biz-services', businessId],
    queryFn: () => getBizServices(businessId),
    staleTime: 30_000,
  })

  const [editing, setEditing] = useState<number | null>(null)
  const [form, setForm] = useState<Partial<BizService>>({})
  const [adding, setAdding] = useState(false)
  const [addForm, setAddForm] = useState<Partial<BizService>>({ isTaxable: true, isActive: true })
  const [saving, setSaving] = useState(false)

  const startEdit = (s: BizService) => { setEditing(s.serviceID); setForm({ ...s }) }

  const saveEdit = async () => {
    if (!editing) return
    setSaving(true)
    try { await updateBizService(editing, form as BizService); invalidate(); onToast('Service updated'); setEditing(null) }
    catch { onToast('Failed to update service', true) }
    finally { setSaving(false) }
  }

  const saveAdd = async () => {
    if (!addForm.serviceName?.trim()) { onToast('Service name is required', true); return }
    setSaving(true)
    try { await createBizService(businessId, addForm); invalidate(); onToast('Service added'); setAdding(false); setAddForm({ isTaxable: true, isActive: true }) }
    catch { onToast('Failed to add service', true) }
    finally { setSaving(false) }
  }

  const F: React.CSSProperties = { padding: '0.3rem 0.55rem', borderRadius: 7, border: '1.5px solid var(--pb-border)', background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)', fontSize: '0.85rem', outline: 'none', width: '100%' }

  const ServiceRow = ({ s }: { s: BizService }) => {
    const isEdit = editing === s.serviceID
    const f = isEdit ? form : s
    return (
      <div style={{ padding: '0.85rem 1.4rem', borderBottom: '1px solid var(--pb-border)' }}>
        {isEdit ? (
          <div>
            <div className="row g-2 mb-2">
              <div className="col-sm-4"><input style={F} value={f.serviceName ?? ''} onChange={e => setForm(p => ({ ...p, serviceName: e.target.value }))} placeholder="Service name" /></div>
              <div className="col-sm-4"><input style={F} value={f.serviceDescription ?? ''} onChange={e => setForm(p => ({ ...p, serviceDescription: e.target.value }))} placeholder="Description (optional)" /></div>
              <div className="col-sm-2"><input style={F} type="number" step="0.01" value={f.serviceRate ?? ''} onChange={e => setForm(p => ({ ...p, serviceRate: parseFloat(e.target.value) || 0 }))} placeholder="Rate" /></div>
              <div className="col-sm-2 d-flex align-items-center gap-2">
                <input type="checkbox" checked={!!(f.isTaxable)} onChange={e => setForm(p => ({ ...p, isTaxable: e.target.checked }))} id={`tax-${s.serviceID}`} />
                <label htmlFor={`tax-${s.serviceID}`} style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginBottom: 0 }}>Taxable</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} style={{ padding: '0.3rem 0.7rem', borderRadius: 7, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--pb-muted)', fontSize: '0.84rem' }}>Cancel</button>
              <SaveBtn onClick={saveEdit} loading={saving} />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{s.serviceName}</div>
              {s.serviceDescription && <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.serviceDescription}</div>}
            </div>
            <span style={{ fontWeight: 600, fontSize: '0.92rem', flexShrink: 0 }}>${s.serviceRate.toFixed(2)}/hr</span>
            {s.isTaxable && <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.15rem 0.45rem', borderRadius: 5, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', flexShrink: 0 }}>HST</span>}
            <IBtn variant="edit" title="Edit" onClick={() => startEdit(s)} />
            <IBtn variant="delete" title="Archive" onClick={() => {
              if (window.confirm(`Archive service "${s.serviceName}"? It will no longer appear when creating invoices.`)) {
                deleteBizService(s.serviceID).then(invalidate).then(() => onToast(`"${s.serviceName}" archived`)).catch(() => onToast('Failed to archive', true))
              }
            }} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--pb-border)' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}><i className="bi bi-tools me-2" style={{ color: 'var(--pb-muted)' }} />Services</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginTop: '0.15rem' }}>{services.length} service{services.length !== 1 ? 's' : ''} &middot; Reusable invoice line items</div>
        </div>
        {!adding && <button onClick={() => setAdding(true)} style={S.addBtn}><i className="bi bi-plus-lg" style={{ fontSize: '0.78rem' }} />Add Service</button>}
      </div>

      {adding && (
        <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--pb-border)', background: 'var(--pb-surface, #fafbff)' }}>
          <div className="row g-2 mb-2">
            <div className="col-sm-4"><div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--pb-muted)', marginBottom: 3 }}>NAME*</div><input style={F} placeholder="e.g. Solution Architecture" value={addForm.serviceName ?? ''} onChange={e => setAddForm(f => ({ ...f, serviceName: e.target.value }))} /></div>
            <div className="col-sm-4"><div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--pb-muted)', marginBottom: 3 }}>DESCRIPTION</div><input style={F} placeholder="Detail text on invoice" value={addForm.serviceDescription ?? ''} onChange={e => setAddForm(f => ({ ...f, serviceDescription: e.target.value }))} /></div>
            <div className="col-sm-2"><div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--pb-muted)', marginBottom: 3 }}>RATE ($/hr)</div><input style={F} type="number" step="0.01" placeholder="150.00" value={addForm.serviceRate ?? ''} onChange={e => setAddForm(f => ({ ...f, serviceRate: parseFloat(e.target.value) || 0 }))} /></div>
            <div className="col-sm-2 d-flex align-items-end gap-2 pb-1"><input type="checkbox" checked={!!addForm.isTaxable} onChange={e => setAddForm(f => ({ ...f, isTaxable: e.target.checked }))} id="new-tax" /><label htmlFor="new-tax" style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', marginBottom: 0 }}>Taxable</label></div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={() => { setAdding(false); setAddForm({ isTaxable: true, isActive: true }) }} style={{ padding: '0.35rem 0.8rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--pb-muted)', fontSize: '0.84rem' }}>Cancel</button>
            <button onClick={saveAdd} disabled={saving} style={{ padding: '0.35rem 0.9rem', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'rgba(99,102,241,0.15)', color: 'var(--pb-primary)', fontWeight: 600, fontSize: '0.84rem' }}>
              {saving ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      {services.map(s => <ServiceRow key={s.serviceID} s={s} />)}
    </div>
  )
}

// ── Financial Institutions: Connect button ────────────────────────────────────

function ConnectBankButton({ onLinked }: { onLinked: () => void }) {
  const qc = useQueryClient()
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  const exchangeMutation = useMutation({
    mutationFn: ({ publicToken }: { publicToken: string }) => exchangePlaidToken(publicToken, true),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['plaidItems'] }); onLinked() },
    onError:    () => setError('Failed to link account. Please try again.'),
  })

  const { open, ready } = usePlaidLink({
    token: linkToken ?? '',
    onSuccess: (publicToken: string) => exchangeMutation.mutate({ publicToken }),
    onExit:    () => { setLinkToken(null); setLoading(false) },
  })

  const handleClick = async () => {
    setError(null); setLoading(true)
    try {
      const result = await createLinkToken(true)
      setLinkToken(result.linkToken)
    } catch {
      setError('Could not start linking. Check API configuration.')
      setLoading(false)
    }
  }

  if (linkToken && ready) open()

  return (
    <div>
      {error && <div style={{ color: 'var(--pb-danger, #ef4444)', fontSize: '0.82rem', marginBottom: '0.4rem' }}>{error}</div>}
      <button className="btn btn-primary btn-sm" onClick={handleClick} disabled={loading || exchangeMutation.isPending}>
        {loading || exchangeMutation.isPending
          ? <><span className="spinner-border spinner-border-sm me-2" />Connecting...</>
          : <><i className="bi bi-bank me-2" />Connect a bank account</>}
      </button>
    </div>
  )
}

// ── Financial Institutions section ────────────────────────────────────────────

function FinancialInstitutionsSection({ onToast }: { onToast: (msg: string, isError?: boolean) => void }) {
  const qc = useQueryClient()

  const { data: items = [], isLoading } = useQuery<PlaidItem[]>({
    queryKey: ['plaidItems'],
    queryFn:  () => getPlaidItems(undefined, false),
    staleTime: 30_000,
  })

  const { data: businesses = [] } = useQuery({ queryKey: ['businesses'], queryFn: getBusinesses, staleTime: 60_000 })

  const [unlinkConfirm,  setUnlinkConfirm]  = useState<string | null>(null)
  const [unlinkLoading,  setUnlinkLoading]  = useState(false)
  const [savingAccount,  setSavingAccount]  = useState<string | null>(null)
  const [editingNickname, setEditingNickname] = useState<string | null>(null)
  const [nicknameInput,   setNicknameInput]   = useState('')
  const [nicknameSaving,  setNicknameSaving]  = useState(false)

  const sortedActiveItems = [...items.filter(i => i.isActive)].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.linkedAt.localeCompare(b.linkedAt)
  )

  const invalidate = () => qc.invalidateQueries({ queryKey: ['plaidItems'] })

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleUnlink = async (itemId: string, name: string) => {
    setUnlinkLoading(true)
    try { await unlinkPlaidAccount(itemId); invalidate(); setUnlinkConfirm(null); onToast(`"${name}" unlinked`) }
    catch { onToast('Failed to unlink institution', true) }
    finally { setUnlinkLoading(false) }
  }

  const handleAccountSetting = async (accountId: string, isPersonal: boolean, syncEnabled: boolean, businessId?: number | null) => {
    setSavingAccount(accountId)
    try { await updateAccountSettings(accountId, { isPersonal, syncEnabled, businessId }); invalidate() }
    catch { onToast('Failed to save account settings', true) }
    finally { setSavingAccount(null) }
  }

  const moveItem = async (itemId: string, direction: 'up' | 'down') => {
    const sorted = [...sortedActiveItems]
    const idx = sorted.findIndex(i => i.itemId === itemId)
    if (idx < 0) return
    const newIdx = direction === 'up' ? idx - 1 : idx + 1
    if (newIdx < 0 || newIdx >= sorted.length) return
    ;[sorted[idx], sorted[newIdx]] = [sorted[newIdx], sorted[idx]]
    try { await reorderPlaidItems(sorted.map(i => i.itemId)); invalidate() }
    catch { onToast('Failed to reorder institutions', true) }
  }

  const moveAccount = async (itemId: string, accountId: string, direction: 'up' | 'down', accounts: PlaidAccountInfo[]) => {
    const sorted = [...accounts].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    const idx = sorted.findIndex(a => a.plaidAccountId === accountId)
    if (idx < 0) return
    const newIdx = direction === 'up' ? idx - 1 : idx + 1
    if (newIdx < 0 || newIdx >= sorted.length) return
    ;[sorted[idx], sorted[newIdx]] = [sorted[newIdx], sorted[idx]]
    try { await reorderPlaidAccounts(itemId, sorted.map(a => a.plaidAccountId)); invalidate() }
    catch { onToast('Failed to reorder accounts', true) }
  }

  const startNicknameEdit = (acc: PlaidAccountInfo) => { setEditingNickname(acc.plaidAccountId); setNicknameInput(acc.nickname ?? '') }
  const cancelNicknameEdit = () => { setEditingNickname(null); setNicknameInput('') }
  const saveNickname = async (accountId: string) => {
    setNicknameSaving(true)
    try { await updateAccountNickname(accountId, nicknameInput.trim() || null); invalidate(); setEditingNickname(null); setNicknameInput('') }
    catch { onToast('Failed to save nickname', true) }
    finally { setNicknameSaving(false) }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const reorderBtn = (disabled: boolean, onClick: () => void, icon: string, title: string) => (
    <button
      onClick={onClick} disabled={disabled} title={title}
      style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, fontSize: '0.65rem', cursor: disabled ? 'default' : 'pointer', color: disabled ? 'var(--pb-border)' : 'var(--pb-muted)' }}
    >
      <i className={`bi ${icon}`} />
    </button>
  )

  const typeDot = (type: string) => {
    const map: Record<string, string> = { credit: '#ef4444', depository: '#22c55e', loan: '#f97316', investment: '#06b6d4' }
    return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: map[type.toLowerCase()] ?? '#94a3b8', flexShrink: 0 }} />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div>
        <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>Financial Institutions</div>
        <p style={{ color: 'var(--pb-muted)', fontSize: '0.88rem', marginBottom: '1rem' }}>
          Manage linked banks and configure each account's module assignment and sync behaviour.
        </p>
        <ConnectBankButton onLinked={invalidate} />
      </div>

      {isLoading && <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>}

      {!isLoading && sortedActiveItems.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--pb-muted)', background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20 }}>
          <i className="bi bi-bank2" style={{ fontSize: '2rem', opacity: 0.35 }} />
          <div className="mt-2 fw-semibold">No linked accounts yet</div>
          <div style={{ fontSize: '0.85rem' }}>Click "Connect a bank account" above to get started.</div>
        </div>
      )}

      {sortedActiveItems.map((item, itemIdx) => {
        const sortedAccounts = [...item.accounts].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        return (
          <div key={item.itemId} style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 20, overflow: 'hidden' }}>

            {/* Institution header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', padding: '0.9rem 1.4rem', borderBottom: '1px solid var(--pb-border)', background: 'var(--pb-surface, #fafbff)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                {/* Institution reorder */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                  {reorderBtn(itemIdx === 0, () => moveItem(item.itemId, 'up'), 'bi-chevron-up', 'Move institution up')}
                  {reorderBtn(itemIdx === sortedActiveItems.length - 1, () => moveItem(item.itemId, 'down'), 'bi-chevron-down', 'Move institution down')}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    <i className="bi bi-bank2 me-2" style={{ color: 'var(--pb-muted)' }} />
                    {item.institutionName ?? 'Unknown institution'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)', marginTop: 2 }}>
                    {item.accounts.length} account{item.accounts.length !== 1 ? 's' : ''} · Last synced:{' '}
                    {item.lastSyncedAt
                      ? new Date(item.lastSyncedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : 'Never'}
                  </div>
                </div>
              </div>
              <div>
                {unlinkConfirm === item.itemId ? (
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--pb-muted)' }}>Confirm unlink?</span>
                    <button className="btn btn-sm btn-danger" disabled={unlinkLoading} onClick={() => handleUnlink(item.itemId, item.institutionName ?? 'this institution')}>
                      {unlinkLoading ? <span className="spinner-border spinner-border-sm" /> : 'Unlink'}
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setUnlinkConfirm(null)}>Cancel</button>
                  </div>
                ) : (
                  <button className="btn btn-sm btn-outline-danger" onClick={() => setUnlinkConfirm(item.itemId)}>
                    <i className="bi bi-x-lg me-1" />Unlink
                  </button>
                )}
              </div>
            </div>

            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr 180px 100px', padding: '0.4rem 1.4rem', borderBottom: '1px solid var(--pb-border)', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--pb-muted)' }}>
              <span />
              <span>Account</span>
              <span style={{ textAlign: 'center' }}>Module</span>
              <span style={{ textAlign: 'center' }}>Sync</span>
            </div>

            {/* Account rows */}
            {sortedAccounts.map((acc, accIdx) => (
              <div
                key={acc.plaidAccountId}
                style={{ display: 'grid', gridTemplateColumns: '28px 1fr 180px 100px', alignItems: 'center', padding: '0.65rem 1.4rem', borderBottom: accIdx < sortedAccounts.length - 1 ? '1px solid var(--pb-border)' : undefined, opacity: savingAccount === acc.plaidAccountId ? 0.55 : 1, transition: 'opacity 0.15s', background: !acc.syncEnabled ? 'rgba(148,163,184,0.04)' : undefined }}
              >
                {/* Account reorder arrows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  {reorderBtn(accIdx === 0, () => moveAccount(item.itemId, acc.plaidAccountId, 'up', item.accounts), 'bi-chevron-up', 'Move account up')}
                  {reorderBtn(accIdx === sortedAccounts.length - 1, () => moveAccount(item.itemId, acc.plaidAccountId, 'down', item.accounts), 'bi-chevron-down', 'Move account down')}
                </div>

                {/* Account name + nickname editing */}
                <div>
                  {editingNickname === acc.plaidAccountId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {typeDot(acc.type)}
                      <input
                        autoFocus
                        value={nicknameInput}
                        onChange={e => setNicknameInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveNickname(acc.plaidAccountId); if (e.key === 'Escape') cancelNicknameEdit() }}
                        disabled={nicknameSaving}
                        placeholder={acc.name}
                        style={{ fontSize: '0.85rem', fontWeight: 500, padding: '0.15rem 0.4rem', borderRadius: 5, border: '1.5px solid var(--pb-primary)', background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)', outline: 'none', width: 160 }}
                      />
                      {nicknameSaving
                        ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, color: 'var(--pb-primary)' }} />
                        : <>
                            <button onClick={() => saveNickname(acc.plaidAccountId)} title="Save" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#16a34a', fontSize: '0.9rem', lineHeight: 1 }}><i className="bi bi-check2" /></button>
                            <button onClick={cancelNicknameEdit}  title="Cancel" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--pb-muted)', fontSize: '0.78rem', lineHeight: 1 }}><i className="bi bi-x-lg" /></button>
                          </>
                      }
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: 500, fontSize: '0.88rem' }}>
                      {typeDot(acc.type)}
                      <span style={{ color: acc.syncEnabled ? 'var(--pb-text)' : 'var(--pb-muted)' }}>{acc.nickname ?? acc.name}</span>
                      {acc.mask && <span style={{ color: 'var(--pb-muted)', fontSize: '0.75rem' }}>••••{acc.mask}</span>}
                      <button onClick={() => startNicknameEdit(acc)} title="Edit nickname" style={{ background: 'none', border: 'none', padding: '0 0.1rem', cursor: 'pointer', color: 'var(--pb-muted)', fontSize: '0.72rem', lineHeight: 1, opacity: 0.55 }}>
                        <i className="bi bi-pencil" />
                      </button>
                      {savingAccount === acc.plaidAccountId && <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, color: 'var(--pb-primary)' }} />}
                    </div>
                  )}
                  <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', marginTop: 1, marginLeft: '1.1rem', textTransform: 'capitalize' }}>
                    {acc.subtype ?? acc.type}
                  </div>
                </div>

                {/* Personal / Business segmented control */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                  <div style={{ display: 'flex', borderRadius: 8, border: '1.5px solid var(--pb-border)', overflow: 'hidden' }}>
                    {([
                      { value: true,  label: 'Personal', icon: 'bi-person'    },
                      { value: false, label: 'Business',  icon: 'bi-briefcase' },
                    ] as { value: boolean; label: string; icon: string }[]).map(opt => (
                      <button
                        key={String(opt.value)}
                        onClick={() => handleAccountSetting(acc.plaidAccountId, opt.value, acc.syncEnabled, opt.value ? null : (acc.businessId ?? null))}
                        disabled={savingAccount === acc.plaidAccountId}
                        style={{ padding: '0.28rem 0.6rem', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, background: acc.isPersonal === opt.value ? '#eef2ff' : 'transparent', color: acc.isPersonal === opt.value ? '#4338ca' : 'var(--pb-muted)', transition: 'background 0.1s, color 0.1s' }}
                      >
                        <i className={`bi ${opt.icon} me-1`} style={{ fontSize: '0.68rem' }} />{opt.label}
                      </button>
                    ))}
                  </div>
                  {!acc.isPersonal && businesses.length > 0 && (
                    <select
                      value={acc.businessId ?? ''}
                      disabled={savingAccount === acc.plaidAccountId}
                      onChange={e => {
                        const bid = e.target.value ? Number(e.target.value) : null
                        handleAccountSetting(acc.plaidAccountId, false, acc.syncEnabled, bid)
                      }}
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.4rem', borderRadius: 6, border: '1.5px solid var(--pb-border)', background: 'var(--pb-card-bg, #fff)', color: acc.businessId ? 'var(--pb-text)' : 'var(--pb-muted)', maxWidth: 150, cursor: 'pointer' }}
                    >
                      <option value="">— assign business —</option>
                      {businesses.map(b => (
                        <option key={b.businessID} value={b.businessID}>{b.businessName}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Sync toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                  <button
                    onClick={() => handleAccountSetting(acc.plaidAccountId, acc.isPersonal, !acc.syncEnabled, acc.businessId)}
                    disabled={savingAccount === acc.plaidAccountId}
                    title={acc.syncEnabled ? 'Disable sync' : 'Enable sync'}
                    style={{ position: 'relative', display: 'inline-flex', width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', padding: 2, background: acc.syncEnabled ? '#6366f1' : 'var(--pb-muted)', transition: 'background 0.2s', flexShrink: 0 }}
                  >
                    <span style={{ position: 'absolute', top: 2, left: acc.syncEnabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
                  </button>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: acc.syncEnabled ? '#6366f1' : 'var(--pb-muted)', minWidth: 20 }}>
                    {acc.syncEnabled ? 'On' : 'Off'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )
      })}

      {/* Footer info */}
      {!isLoading && (
        <div style={{ padding: '0.65rem 0.9rem', background: 'rgba(148,163,184,0.06)', borderRadius: 8, border: '1px solid var(--pb-border)', fontSize: '0.82rem', color: 'var(--pb-muted)', lineHeight: 1.6 }}>
          <i className="bi bi-info-circle me-2" />
          Transactions are pulled going back up to 90 days on first connection.
          Balances refresh automatically when transactions are saved.
          Pending transactions update on the next sync; cancelled ones are removed automatically.
        </div>
      )}
    </div>
  )
}

// ── Settings (main page) ──────────────────────────────────────────────────────
type PersonalSection = 'financial-institutions' | 'categories' | 'tags' | 'security'
type BizSection      = 'financial-institutions' | 'general' | 'business' | 'services' | 'clients' | 'categories' | 'tags' | 'security'

export default function Settings() {
  const { mode }                                 = useMode()
  const { selectedBusinessId, selectedBusiness } = useBusinessContext()
  const isBusiness                               = mode === 'business'
  const bizId                                    = selectedBusinessId

  const [personalSection, setPersonalSection] = useState<PersonalSection>('financial-institutions')
  const [bizSection,      setBizSection]      = useState<BizSection>('financial-institutions')
  const { toasts, show: showToast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', isBusiness ? bizId : null],
    queryFn:  () => getSettings(isBusiness ? bizId : null),
    staleTime: 60_000,
    enabled:   !isBusiness || bizId != null,
  })

  const navLink = (key: string, label: string, icon: string, active: boolean, onClick: () => void) => (
    <a key={key} href="#" onClick={e => { e.preventDefault(); onClick() }}
      style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.6rem 0.85rem', borderRadius: 10, fontWeight: active ? 600 : 500, fontSize: '0.92rem', color: active ? '#4338ca' : 'var(--pb-muted)', background: active ? '#eef2ff' : 'transparent', textDecoration: 'none', transition: 'background 0.12s ease, color 0.12s ease' }}
    >
      <i className={`bi ${icon}`} />{label}
    </a>
  )

  const sectionLabel = (text: string) => (
    <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--pb-muted)', padding: '0.6rem 0.85rem 0.25rem', marginTop: '0.5rem' }}>{text}</div>
  )

  const renderContent = () => {
    // Security section doesn't need the settings data — render immediately
    if (personalSection === 'security' && !isBusiness) return <SecuritySection onToast={showToast} />
    if (bizSection      === 'security' &&  isBusiness) return <SecuritySection onToast={showToast} />

    if (isLoading) return <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>
    if (error || !data) return <div className="pb-empty"><i className="bi bi-exclamation-triangle me-2" />Failed to load settings.</div>
    if (!isBusiness) {
      switch (personalSection) {
        case 'financial-institutions': return <FinancialInstitutionsSection onToast={showToast} />
        case 'categories': return <CategoriesSection categories={data.categories} onToast={showToast} />
        case 'tags':       return <TagsSection tags={data.tags} onToast={showToast} />
        case 'security':   return <SecuritySection onToast={showToast} />
      }
    } else {
      if (!selectedBusiness) return <div className="pb-empty">No business selected.</div>
      switch (bizSection) {
        case 'financial-institutions': return <FinancialInstitutionsSection onToast={showToast} />
        case 'general':    return <GeneralSection onToast={showToast} />
        case 'business':   return <BusinessSection onToast={showToast} />
        case 'services':   return <ServicesManager key={bizId} businessId={bizId!} onToast={showToast} />
        case 'clients':    return <ClientsManager  key={bizId} businessId={bizId!} onToast={showToast} />
        case 'categories': return <CategoriesSection categories={data.categories} onToast={showToast} businessId={bizId} />
        case 'tags':       return <TagsSection tags={data.tags} onToast={showToast} businessId={bizId} />
        case 'security':   return <SecuritySection onToast={showToast} />
      }
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.75rem' }}>
        <span style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.2rem', background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)', boxShadow: '0 10px 24px -10px rgba(99,102,241,0.55)' }}>
          <i className="bi bi-gear-fill" />
        </span>
        <div>
          <h1 className="pb-page-title mb-0">Settings</h1>
          <p style={{ color: 'var(--pb-muted)', fontSize: '0.9rem', margin: 0 }}>Manage your app preferences and data.</p>
        </div>
      </div>
      <div className="row g-4">
        <div className="col-lg-3 col-md-4">
          <nav style={{ background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)', borderRadius: 16, padding: '0.5rem', position: 'sticky', top: 80 }}>
            {!isBusiness && <>
              {navLink('financial-institutions', 'Financial Institutions', 'bi-bank2',       personalSection === 'financial-institutions', () => setPersonalSection('financial-institutions'))}
              {sectionLabel('Personal')}
              {navLink('categories', 'Categories', 'bi-tags-fill',        personalSection === 'categories', () => setPersonalSection('categories'))}
              {navLink('tags',       'Tags',       'bi-bookmark-fill',     personalSection === 'tags',       () => setPersonalSection('tags'))}
              {sectionLabel('Account')}
              {navLink('security',   'Security',  'bi-shield-lock-fill',  personalSection === 'security',   () => setPersonalSection('security'))}
            </>}
            {isBusiness && <>
              {navLink('financial-institutions', 'Financial Institutions', 'bi-bank2',       bizSection === 'financial-institutions', () => setBizSection('financial-institutions'))}
              {sectionLabel('Business')}
              {navLink('general',    'General',          'bi-sliders',       bizSection === 'general',    () => setBizSection('general'))}
              {navLink('business',   'Business Profile', 'bi-building',      bizSection === 'business',   () => setBizSection('business'))}
              {navLink('services',   'Services',         'bi-briefcase',     bizSection === 'services',   () => setBizSection('services'))}
              {navLink('clients',    'Clients',          'bi-people',        bizSection === 'clients',    () => setBizSection('clients'))}
              {navLink('categories', 'Categories',       'bi-tags-fill',        bizSection === 'categories', () => setBizSection('categories'))}
              {navLink('tags',       'Tags',             'bi-bookmark-fill',    bizSection === 'tags',       () => setBizSection('tags'))}
              {sectionLabel('Account')}
              {navLink('security',   'Security',         'bi-shield-lock-fill', bizSection === 'security',   () => setBizSection('security'))}
            </>}
          </nav>
        </div>
        <div className="col-lg-9 col-md-8">{renderContent()}</div>
      </div>
      <ToastContainer toasts={toasts} />
    </div>
  )
}
